import React, { useMemo, useEffect, useState } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ProfileScreen from '../screens/ProfileScreen';
import { CacheNavigator, BuyNavigator } from './index';
import { MainTabParamList } from '../types/navigation';
import { RouteProp } from '@react-navigation/native';
import { useTheme } from '../styles/ThemeProvider';
import { isUserAdmin } from '../utils/userPermissions';

const Tab = createBottomTabNavigator<MainTabParamList>();

function AddItemPlaceholder() {
  return null;
}

export default function TabNavigator() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  // Buy tab is admin-only pre-launch. Resolve once on mount; null while
  // pending so the tab never flashes for non-admins.
  //
  // Trust model caveat: `isUserAdmin()` currently reads
  // `user.user_metadata.role`, which is user-editable in Supabase. The
  // gate is defence-in-depth for a pre-launch preview that's read-only
  // — surface added here adds no new write paths — but the long-term
  // fix is to migrate admin gating to `app_metadata` (server-controlled)
  // or to a backend RPC that checks against a server-side admin list.
  // Same caveat applies to every other call site (ProfileScreen,
  // ItemDetailScreen, WebNavHeader, …); tracked as a follow-up rather
  // than fixed inline here.
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    isUserAdmin().then(setIsAdmin);
  }, []);
  const styles = useMemo(
    () =>
      StyleSheet.create({
        tabBar: {
          backgroundColor: theme.colors.backgroundPrimary,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
          height: 60,
          paddingBottom: 5,
        },
        tabBarLabel: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          letterSpacing: 0,
        },
        tabBarItem: {
          paddingTop: 5,
        },
      }),
    [theme],
  );

  return (
    <Tab.Navigator
      screenOptions={({ route }: { route: RouteProp<MainTabParamList, keyof MainTabParamList> }) => ({
        headerShown: false,
        tabBarActiveTintColor: theme.colors.accent,
        tabBarInactiveTintColor: theme.colors.textSecondary,
        tabBarStyle: Platform.OS === 'web'
          ? { display: 'none' }
          : {
              ...styles.tabBar,
              height: 60 + insets.bottom,
              paddingBottom: insets.bottom + 5,
            },
        tabBarLabelStyle: styles.tabBarLabel,
        tabBarItemStyle: styles.tabBarItem,
        tabBarIcon: ({ color, size }: { color: string; size: number }) => {
          let iconName = '';
          if (route.name === 'Cache') iconName = 'albums';
          if (route.name === 'AddItem') iconName = 'add-circle';
          if (route.name === 'Buy') iconName = 'cart-outline';
          if (route.name === 'Profile') iconName = 'person';
          return <Ionicons name={iconName as any} size={24} color={color} />;
        },
      })}
    >
      <Tab.Screen
        name="Cache"
        component={CacheNavigator}
        options={{ title: 'Cache' }}
        listeners={({ navigation }) => ({
          tabPress: (e) => {
            const state = navigation.getState();
            const cacheRoute = state.routes.find(r => r.name === 'Cache');

            if (cacheRoute && state.index === state.routes.indexOf(cacheRoute)) {
              navigation.navigate('Cache', { screen: 'CacheList' } as any);
            }
          },
        })}
      />
      <Tab.Screen
        name="AddItem"
        component={AddItemPlaceholder}
        options={{ title: 'Add' }}
        listeners={({ navigation }) => ({
          tabPress: (e) => {
            e.preventDefault();
            const parent = navigation.getParent();
            if (parent) {
              parent.navigate('AddItem' as never);
            }
          },
        })}
      />
      {isAdmin && (
        <Tab.Screen
          name="Buy"
          component={BuyNavigator}
          options={{ title: 'Buy' }}
        />
      )}
      <Tab.Screen
        name="Profile"
        component={ProfileScreen}
        options={{ title: 'Profile' }}
      />
    </Tab.Navigator>
  );
}
